/**
 * 认证钩子
 */
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { login as apiLogin, logout as apiLogout, isAuthenticated, refreshToken, getCurrentUser } from "../api/auth";
import { isAuthRefreshDeterministicError, isAuthRefreshUnknownResultError } from "../api/authRefreshOrchestrator";
import { parseStoredUserInfo, readAuthSnapshot, subscribeAuthState, writeUserInfoAuthState } from "../api/authState";
import { getDefaultEmailConfig } from "../api/userEmailConfigApi";
import { buildCurrentPath, buildPortalLoginUrl } from "../utils/authRedirect";
import {
    clearStoredActiveOrganizationId,
    getStoredActiveOrganizationId,
    resolveActiveOrganization,
    setStoredActiveOrganizationId,
} from "../utils/organizationScope";

// 创建认证上下文
export const AuthContext = createContext(null);

const toSafeErrorMeta = (error) => ({
    status: error?.response?.status,
    code: error?.response?.data?.detail?.code || error?.code,
});

// 认证提供者组件
export const AuthProvider = ({ children, messageApi }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userEmailConfig, setUserEmailConfig] = useState(null); // 用户邮箱配置
    const [defaultOrganization, setDefaultOrganization] = useState(null);
    const [assignedOrganizations, setAssignedOrganizations] = useState([]);
    const [accessibleOrganizations, setAccessibleOrganizations] = useState([]);
    const [activeOrganization, setActiveOrganization] = useState(null);

    const syncOrganizationScope = useCallback((userInfo) => {
        const assignedOrganizations = Array.isArray(userInfo?.assigned_organizations) ? userInfo.assigned_organizations : [];
        const accessibleOrganizations = Array.isArray(userInfo?.accessible_organizations) ? userInfo.accessible_organizations : [];
        const selectableOrganizations = accessibleOrganizations.length > 0 ? accessibleOrganizations : assignedOrganizations;
        const preferredOrganization = userInfo?.default_organization || null;
        const resolvedActiveOrganization = resolveActiveOrganization(selectableOrganizations, getStoredActiveOrganizationId(), preferredOrganization?.id);

        setAssignedOrganizations(assignedOrganizations);
        setAccessibleOrganizations(accessibleOrganizations);
        setDefaultOrganization(preferredOrganization);
        setActiveOrganization(resolvedActiveOrganization);

        if (resolvedActiveOrganization) {
            setStoredActiveOrganizationId(resolvedActiveOrganization.id);
        } else {
            clearStoredActiveOrganizationId();
        }
    }, []);

    const clearOrganizationScope = useCallback(() => {
        setDefaultOrganization(null);
        setAssignedOrganizations([]);
        setAccessibleOrganizations([]);
        setActiveOrganization(null);
        clearStoredActiveOrganizationId();
    }, []);

    useEffect(() => {
        return subscribeAuthState((snapshot, event) => {
            if (snapshot.status === "anonymous") {
                setUser(null);
                setUserEmailConfig(null);
                clearOrganizationScope();
                return;
            }

            const storedUser = parseStoredUserInfo(snapshot);
            if (storedUser) {
                setUser(storedUser);
                syncOrganizationScope(storedUser);
            }
        });
    }, [clearOrganizationScope, syncOrganizationScope]);

    const setActiveOrganizationId = useCallback(
        (organizationId) => {
            const selectableOrganizations = accessibleOrganizations.length > 0 ? accessibleOrganizations : assignedOrganizations;
            const nextOrganization = resolveActiveOrganization(selectableOrganizations, organizationId, defaultOrganization?.id);

            if (!nextOrganization || nextOrganization.id !== Number(organizationId)) {
                return false;
            }

            setActiveOrganization(nextOrganization);
            setStoredActiveOrganizationId(nextOrganization.id);
            return true;
        },
        [accessibleOrganizations, assignedOrganizations, defaultOrganization],
    );

    const parseLoginError = useCallback((error) => {
        const detail = error?.response?.data?.detail;
        const structuredDetail = detail && typeof detail === "object" ? detail : null;
        const status = error?.response?.status;
        const code = structuredDetail?.code || null;
        const captchaRequired = Boolean(structuredDetail?.captcha_required);
        const retryAfterSeconds = structuredDetail?.retry_after_seconds;
        let message = structuredDetail?.message || "登录失败，请检查用户名和密码";

        if (error?.message && (error.message.includes("502") || error.message.includes("超时") || error.message.includes("网络错误"))) {
            message = error.message;
        } else if (status === 401) {
            message = structuredDetail?.message || "账号或密码错误";
        } else if (captchaRequired) {
            message = structuredDetail?.message || "需要验证码验证后继续登录";
        } else if (status === 423) {
            message = structuredDetail?.message || "登录暂时受限，请稍后再试";
        } else if (status === 429) {
            message = structuredDetail?.message || "登录尝试次数过多，请稍后再试";
        } else if (status === 400 && typeof detail === "string") {
            message = detail;
        } else if (status === 500) {
            message = "服务器内部错误，请联系管理员";
        } else if (status === 502) {
            message = "服务器网关错误，后端服务可能暂时不可用";
        } else if (status === 503) {
            message = structuredDetail?.message || "服务暂时不可用，可能正在维护中";
        } else if (status === 504) {
            message = "服务器网关超时，请稍后再试";
        } else if (error?.request) {
            message = "无法连接到服务器，请检查网络连接";
        }

        return { code, captchaRequired, retryAfterSeconds, message, status };
    }, []);

    // 加载用户邮箱配置
    const loadUserEmailConfig = useCallback(async () => {
        try {
            const response = await getDefaultEmailConfig();
            // 后端返回 EmailConfigOperationResponse: { success, message, data }
            // data 为 null 表示用户尚未配置（200 正常响应，非 404）
            if (response.data?.data) {
                setUserEmailConfig(response.data.data);
            } else {
                setUserEmailConfig(null);
            }
        } catch (error) {
            console.error("加载邮箱配置失败:", toSafeErrorMeta(error));
            setUserEmailConfig(null);
        }
    }, []);

    // 初始化验证用户状态
    useEffect(() => {
        const initAuth = async () => {
            try {
                setLoading(true);

                // 检查用户是否已登录
                if (isAuthenticated()) {
                    try {
                        // 始终从服务器获取最新的用户信息，包括权限
                        const updatedUserInfo = await getCurrentUser();
                        setUser(updatedUserInfo);
                        syncOrganizationScope(updatedUserInfo);

                        // 更新用户信息到当前使用的存储中
                        writeUserInfoAuthState(updatedUserInfo, { entrypoint: "hydration-user" });

                        // 加载用户邮箱配置
                        await loadUserEmailConfig();
                    } catch (error) {
                        console.error("获取用户信息失败:", toSafeErrorMeta(error));

                        // 如果获取失败，尝试使用本地存储的用户信息
                        const userInfo = parseStoredUserInfo();
                        setUser(userInfo);
                        syncOrganizationScope(userInfo);
                    }
                } else {
                    // 尝试使用刷新令牌
                    const refreshTokenExist = readAuthSnapshot().refreshToken;
                    if (refreshTokenExist) {
                        try {
                            await refreshToken({ entrypoint: "hydration", force: true });
                            // 刷新令牌成功后，获取最新用户信息
                            const updatedUserInfo = await getCurrentUser();
                            setUser(updatedUserInfo);
                            syncOrganizationScope(updatedUserInfo);

                            writeUserInfoAuthState(updatedUserInfo, { entrypoint: "hydration-user" });

                            // 加载用户邮箱配置
                            await loadUserEmailConfig();
                        } catch (refreshError) {
                            if (isAuthRefreshDeterministicError(refreshError)) {
                                setUser(null);
                                clearOrganizationScope();
                            } else if (isAuthRefreshUnknownResultError(refreshError)) {
                                const userInfo = parseStoredUserInfo();
                                if (userInfo) {
                                    setUser(userInfo);
                                    syncOrganizationScope(userInfo);
                                    setError("认证恢复结果暂不确定，系统将继续尝试恢复会话。");
                                } else {
                                    setUser(null);
                                    clearOrganizationScope();
                                }
                            } else {
                                setUser(null);
                                clearOrganizationScope();
                            }
                            console.error("刷新令牌失败:", toSafeErrorMeta(refreshError));
                        }
                    } else {
                        setUser(null);
                        clearOrganizationScope();
                    }
                }
            } catch (error) {
                console.error("认证初始化错误:", toSafeErrorMeta(error));
                setError(error);
                setUser(null);
                clearOrganizationScope();
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, [clearOrganizationScope, loadUserEmailConfig, syncOrganizationScope]);

    // ── Plan B: 主动续期定时器 ──
    // 每 60 秒检查 token 是否已消耗超过 80% 生命周期，如果是则主动续期
    // 使用轮询（而非一次性 setTimeout）以自动适应来自 axios 拦截器等外部源的 token 更新
    useEffect(() => {
        if (!user) return;

        // 当 token 已消耗超过此比例的生命周期时触发续期
        const TOKEN_REFRESH_THRESHOLD = 0.8;
        const CHECK_INTERVAL_MS = 60 * 1000;

        const checkAndRefresh = async () => {
            const snapshot = readAuthSnapshot();
            const expiresAtStr = snapshot.tokenExpiresAt;
            const issuedAtStr = snapshot.tokenIssuedAt;

            if (!expiresAtStr || !issuedAtStr) return;

            const expiresAt = parseInt(expiresAtStr, 10);
            const issuedAt = parseInt(issuedAtStr, 10);
            const now = Math.floor(Date.now() / 1000);

            const totalLifetime = expiresAt - issuedAt;
            const refreshPoint = issuedAt + Math.floor(totalLifetime * TOKEN_REFRESH_THRESHOLD);

            // 已达到续期阈值且 token 尚未过期 → 执行续期
            if (now >= refreshPoint && now < expiresAt) {
                try {
                    await refreshToken({ entrypoint: "proactive", force: true });
                } catch (e) {
                    if (isAuthRefreshDeterministicError(e)) {
                        setUser(null);
                        clearOrganizationScope();
                    }
                    // 续期失败不做强制动作，等待下次检查或 axios 拦截器兜底
                    console.error("Token 主动续期失败（将在下次检查时重试）:", toSafeErrorMeta(e));
                }
            }
        };

        const interval = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);
        // 首次立即检查（覆盖页面刷新后已过半衰期的场景）
        checkAndRefresh();

        return () => clearInterval(interval);
    }, [clearOrganizationScope, user]);

    // 登录处理
    const login = useCallback(
        async (credentials) => {
            try {
                setLoading(true);
                setError(null);
                const response = await apiLogin(credentials);

                // 登录成功后，获取完整的用户信息，包括权限
                try {
                    const updatedUserInfo = await getCurrentUser();
                    setUser(updatedUserInfo);
                    syncOrganizationScope(updatedUserInfo);
                    writeUserInfoAuthState(updatedUserInfo, { entrypoint: "login-user" });
                    // 加载用户邮箱配置
                    await loadUserEmailConfig();
                } catch (userError) {
                    console.error("获取用户信息失败:", toSafeErrorMeta(userError));
                    // 如果获取失败，使用登录响应中的用户信息
                    const userInfo = response.user || parseStoredUserInfo();
                    setUser(userInfo);
                    syncOrganizationScope(userInfo);
                }

                if (messageApi) {
                    messageApi.success("登录成功");
                }
                return { ok: true };
            } catch (error) {
                const loginError = parseLoginError(error);
                console.error("登录错误:", { status: loginError.status, code: loginError.code });

                setError(loginError.message);
                if (messageApi) {
                    messageApi.error(loginError.message);
                }
                return { ok: false, ...loginError };
            } finally {
                setLoading(false);
            }
        },
        [loadUserEmailConfig, messageApi, parseLoginError, syncOrganizationScope],
    );

    // 登出处理
    const logout = useCallback(async () => {
        // auth.js 的 logout 现在需先调后端撤销 refresh session 再清理跳转；
        // 必须 await 确保后端请求完成，否则 setUser(null) 触发的 useRequireAuth 跳转会中断 pending 的 logout 请求。
        await apiLogout();
        setUser(null);
        setUserEmailConfig(null); // 清除邮箱配置
        clearOrganizationScope();
        if (messageApi) {
            messageApi.success("已安全退出登录");
        }
    }, [clearOrganizationScope, messageApi]);

    // 检查是否有特定权限
    const hasPermission = useCallback(
        (permissionName) => {
            if (!user) return false;
            if (user.is_superuser) return true;

            const permissions = user.permissions || [];
            return permissions.includes(permissionName);
        },
        [user],
    );

    // 检查是否有多个权限中的任意一个
    const hasAnyPermission = useCallback(
        (permissionNames) => {
            if (!user) return false;
            if (user.is_superuser) return true;

            const permissions = user.permissions || [];
            return permissionNames.some((name) => permissions.includes(name));
        },
        [user],
    );

    // 检查是否有所有指定的权限
    const hasAllPermissions = useCallback(
        (permissionNames) => {
            if (!user) return false;
            if (user.is_superuser) return true;

            const permissions = user.permissions || [];
            return permissionNames.every((name) => permissions.includes(name));
        },
        [user],
    );

    // 暴露的上下文值
    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        userEmailConfig, // 用户邮箱配置
        loadUserEmailConfig, // 重新加载邮箱配置的方法（用于邮箱配置页面更新后刷新）
        defaultOrganization,
        assignedOrganizations,
        accessibleOrganizations,
        activeOrganization,
        setActiveOrganizationId,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 使用认证的自定义钩子
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth 必须在 AuthProvider 内使用");
    }
    return context;
};

// 受保护路由钩子
export const useRequireAuth = (redirectTo) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!auth.loading && !auth.isAuthenticated) {
            const nextPath = buildCurrentPath(location);
            if (!redirectTo) {
                window.location.replace(buildPortalLoginUrl(nextPath));
                return;
            }

            navigate(redirectTo, {
                replace: true,
                state: { from: nextPath },
            });
        }
    }, [auth.loading, auth.isAuthenticated, navigate, redirectTo, location]);

    return auth;
};
